import { DefaultEdgeOptions, MarkerType } from 'reactflow'

import { Edge, Node, NodeType, Pipeline } from '@ui/__generated__/graphql'
import { DEFAULT_PIPELINE_WORKFLOW_NAME } from '@ui/constants/pipelinesWorkflow'
import { SourceHandleEnum, TargetHandleEnum } from '@ui/interfaces'
import { uuid } from '@ui/lib/utils'
import dayjs from 'dayjs'

import { createEdge } from './createEdge'
import { createNode } from './createNode'

// initial setup: one workflow node and a placeholder node
// placeholder nodes can be turned into a workflow node by click

export const codeDefault = `
/***
* You have access to the following inputs:
*  - \`metadata\`:  { timestamp, datatype }
*  - \`record\`:      { ... }
*
*  Play around with the below snippet, or try to
*  console.log('metadata', JSON.stringify(record, undefined, 4))
*
*/
// These are the key attributes of an opentelemetry formatted record
const { attributes, resource, body } = record

attributes.datable = 'can manipulate your data!'

return {
  ...record,
  attributes
}

`

export const logNodeDefaultCode = `
/***
* You have access to the following inputs:
*  - \`metadata\`:  { timestamp, datatype }
*    -> datatype is a string, and can be 'logs' or 'traces'
*  - \`record\`:    { resource, body, ... }
*/

// These are the key attributes of an opentelemetry formatted record
const { attributes, resource, body } = record
const { timestamp, datatype } = metadata

// Here we only allow records tagged as 'logs' to pass through,
// we set the return value to null, we effectively filter out non-log data. 
if(datatype === 'log') {
  return record
}
  
return null
`
export const spanNodeDefaultCode = `
/***
* You have access to the following inputs:
*  - \`metadata\`:  { timestamp, datatype }
*    -> datatype is a string, and can be 'logs' or 'traces'
*  - \`record\`:    { resource, body, ... }
*/

// These are the key attributes of an opentelemetry formatted record
const { attributes, resource, body } = record
const { timestamp, datatype } = metadata

// Here we only allow records tagged as 'traces' to pass through,
// we set the return value to null, we effectively filter out non-trace data. 
if(datatype === 'span') {
  return record
}
  
return null
`

export const createDefaultPipeline = (withSteps: boolean, newPipelineId?: string): Pipeline => {
  const firehoseNodeId = uuid()
  const codeNodeId = uuid()
  const transformNodeId = uuid()

  const defaultNodes: Node[] = [
    createNode({
      nodeId: firehoseNodeId,
      nodeType: NodeType.Firehose,
    }) as Node,
    createNode({
      nodeId: codeNodeId,
      nodeType: NodeType.Code,
      description: 'Transform your data using code',
    }) as Node,
  ]

  if (withSteps) {
    defaultNodes.push(
      createNode({
        nodeId: transformNodeId,
        nodeType: NodeType.Transform,
        description: 'Transform your data using code',
      }) as Node
    )
  }

  const defaultEdges: Edge[] = [
    // Firehose -> Code
    createEdge({
      source: firehoseNodeId,
      target: codeNodeId,
      sourceHandle: SourceHandleEnum.SOURCE_LOGS,
      targetHandle: TargetHandleEnum.TARGET_LOGS,
    }) as Edge,
    createEdge({
      source: firehoseNodeId,
      target: codeNodeId,
      sourceHandle: SourceHandleEnum.SOURCE_SPANS,
      targetHandle: TargetHandleEnum.TARGET_SPANS,
    }) as Edge,
    createEdge({
      source: firehoseNodeId,
      target: codeNodeId,
      sourceHandle: SourceHandleEnum.SOURCE_METRICS,
      targetHandle: TargetHandleEnum.TARGET_METRICS,
    }) as Edge,
  ]

  // Code -> Transform
  if (withSteps) {
    defaultEdges.push(
      createEdge({
        source: codeNodeId,
        target: transformNodeId,
        sourceHandle: SourceHandleEnum.SOURCE_LOGS,
        targetHandle: TargetHandleEnum.TARGET_LOGS,
      }) as Edge,
      createEdge({
        source: codeNodeId,
        target: transformNodeId,
        sourceHandle: SourceHandleEnum.SOURCE_SPANS,
        targetHandle: TargetHandleEnum.TARGET_SPANS,
      }) as Edge,
      createEdge({
        source: codeNodeId,
        target: transformNodeId,
        sourceHandle: SourceHandleEnum.SOURCE_METRICS,
        targetHandle: TargetHandleEnum.TARGET_METRICS,
      }) as Edge
    )
  }

  return {
    id: newPipelineId || uuid(),
    name: DEFAULT_PIPELINE_WORKFLOW_NAME,
    nodes: defaultNodes,
    edges: defaultEdges,
    isPrimary: false,
    enabled: true,
    assignedSources: [],
    lastUpdatedBy: 'SYS',
    createdBy: 'SYS',
    createdTimestamp: dayjs().toISOString(),
  }
}

export const defaultEdgeOptions: DefaultEdgeOptions = {
  markerEnd: { type: MarkerType.ArrowClosed, width: 1, height: 20 },
}
